import React, { useState, useRef } from 'react';
import { Button, Input, message, Row, Tag } from 'antd';
import { handleUploadFilesToS3 } from '../utils/task.utils';
import { CodeOutlined, PaperClipOutlined, PictureOutlined } from '@ant-design/icons'; // Assuming you're using Ant Design icons
import FileDropPasteArea from '../utils/FileDropPasteArea';
import { allowedFileExtensions, allowedImageExtensions } from '../configs/task.config';

const TaskEditableCommentDetails = ({
  comment,
  isEditing,
  setIsEditing,
  refetchTask,
  updateComment,
  onTaskUpdate,
}) => {
  const textareaRef = useRef(null);
  const [editedComment, setEditedComment] = useState(comment.comment || '');
  const [attachedFiles, setAttachedFiles] = useState(comment.attachedFiles || []);
  const [attachedImages, setAttachedImages] = useState(comment.attachedImages || []);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const [buttonDisabled, setButtonDisabled] = useState(false);

  // Handle file upload button click
  const handleUploadFileClick = () => {
    fileInputRef.current.click();
  };

  // Handle image upload button click
  const handleUploadImageClick = () => {
    imageInputRef.current.click();
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedFileExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Images are not allowed in this field.');
      e.target.value = null;
      return;
    }
    setAttachedFiles([...attachedFiles, ...files]);
    e.target.value = null;
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedImageExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Only image files are allowed.');
      e.target.value = null;
      return;
    }
    setAttachedImages([...attachedImages, ...files]);
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    const file = attachedFiles[index];
    if (file.id) {
      setFilesToRemove((prev) => [...prev, file.id]);
    }
    const updatedFiles = [...attachedFiles];
    updatedFiles.splice(index, 1);
    setAttachedFiles(updatedFiles);
  };

  const handleRemoveImage = (index) => {
    const image = attachedImages[index];
    if (image.id) {
      setImagesToRemove((prev) => [...prev, image.id]);
    }
    const updatedImages = [...attachedImages];
    updatedImages.splice(index, 1);
    setAttachedImages(updatedImages);
  };

  const handleInsertCode = () => {
    const textarea =
      textareaRef.current &&
      textareaRef.current.resizableTextArea &&
      textareaRef.current.resizableTextArea.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = editedComment || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;

    if (selectedText) {
      // Wrap selected text with code blocks
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      // Insert empty code block
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    // Update form value
    setEditedComment(newValue);

    // Set cursor position after update
    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  // Save changes
  const handleSave = async () => {
    try {
      setButtonDisabled(true);
      message.loading('Saving changes...', 0);
      const existingFiles = attachedFiles.filter((file) => file.id);
      const newFiles = attachedFiles.filter((file) => !file.id);
      const existingImages = attachedImages.filter((image) => image.id);
      const newImages = attachedImages.filter((image) => !image.id);

      // Upload new files/images
      const uploadedFiles = await handleUploadFilesToS3(newFiles);
      const uploadedImages = await handleUploadFilesToS3(newImages);

      // Prepare update data
      const updateData = {
        comment: editedComment,
      };

      // Handle files
      const filesUpdate = {};
      if (filesToRemove.length > 0) {
        filesUpdate.disconnect = filesToRemove.map((id) => ({ id }));
      }
      if (existingFiles.length > 0) {
        filesUpdate.connect = existingFiles.map((file) => ({ id: file.id }));
      }
      if (uploadedFiles.length > 0) {
        filesUpdate.create = uploadedFiles;
      }
      if (Object.keys(filesUpdate).length > 0) {
        updateData.attachedFiles = filesUpdate;
      }

      // Handle images
      const imagesUpdate = {};
      if (imagesToRemove.length > 0) {
        imagesUpdate.disconnect = imagesToRemove.map((id) => ({ id }));
      }
      if (existingImages.length > 0) {
        imagesUpdate.connect = existingImages.map((image) => ({ id: image.id }));
      }
      if (uploadedImages.length > 0) {
        imagesUpdate.create = uploadedImages;
      }
      if (Object.keys(imagesUpdate).length > 0) {
        updateData.attachedImages = imagesUpdate;
      }

      // Update task
      await updateComment({
        variables: {
          where: { id: comment.id },
          data: updateData,
        },
      });

      // Refresh and exit edit mode
      await refetchTask();

      // Notify parent to refresh task list
      if (onTaskUpdate) {
        onTaskUpdate();
      }

      message.destroy(); // Remove loading message
      message.success('Saved Succesfully!');
      setIsEditing(false);
    } catch (error) {
      message.destroy();
      message.error('Error saving changes');
      setButtonDisabled(false);
    }
  };

  const handleCancel = () => {
    setEditedComment(comment.comment);
    setAttachedFiles(comment.attachedFiles || []);
    setAttachedImages(comment.attachedImages || []);
    setIsEditing(false);
  };

  if (!isEditing) {
    return null;
  }

  return (
    <div style={{ marginLeft: 20, marginTop: 8 }}>
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        accept=".doc, .docx, .pdf, .txt, .xls, .xlsx, .ppt, .pptx, .csv"
        onChange={handleFileChange}
      />
      <input
        type="file"
        ref={imageInputRef}
        style={{ display: 'none' }}
        multiple
        accept=".jpg, .jpeg, .png, .gif, .avif"
        onChange={handleImageChange}
      />

      <FileDropPasteArea
        onImagesAdded={(newImages) => {
          const currentImages = attachedImages || [];
          setAttachedImages([...currentImages, ...newImages]);
        }}
        onFilesAdded={(newFiles) => {
          const currentFiles = attachedFiles || [];
          setAttachedFiles([...currentFiles, ...newFiles]);
        }}
      >
        {(((attachedFiles && attachedFiles.length > 0) ||
          (attachedImages && attachedImages.length > 0)) && (
          <>
            <div
              style={{
                backgroundColor: '#fafafa',
                paddingInline: '8px',
                paddingBlockStart: '5px',
                paddingBlockEnd: '30px',
                border: '1px solid #eee',
                marginBottom: '8px',
              }}
            >
              {/* Render tags inside the text box */}
              {attachedFiles && attachedFiles.length > 0 && (
                <Row
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    width: '100%',
                    padding: '4px',
                  }}
                >
                  {attachedFiles.map((file, index) => (
                    <Tag
                      closable
                      key={index}
                      onClose={() => handleRemoveFile(index)}
                      icon={<PaperClipOutlined />}
                      style={{
                        margin: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      {file.name
                        ? file.name.length > 30
                          ? `${file.name.slice(0, 30)}...`
                          : file.name
                        : file.originalName &&
                          (file.originalName.length > 30
                            ? `${file.originalName.slice(0, 30)}...`
                            : file.originalName)}
                    </Tag>
                  ))}
                </Row>
              )}
              {attachedImages && attachedImages.length > 0 && (
                <Row
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    width: '100%',
                    padding: '4px',
                  }}
                >
                  {attachedImages.map((file, index) => (
                    <Tag
                      closable
                      key={index}
                      onClose={() => handleRemoveImage(index)}
                      icon={<PictureOutlined />}
                      style={{
                        margin: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      {file.name
                        ? file.name.length > 30
                          ? `${file.name.slice(0, 30)}...`
                          : file.name
                        : file.originalName &&
                          (file.originalName.length > 30
                            ? `${file.originalName.slice(0, 30)}...`
                            : file.originalName)}
                    </Tag>
                  ))}
                </Row>
              )}
            </div>
            <div style={{ flexGrow: 1 }}>
              <Input.TextArea
                ref={textareaRef}
                value={editedComment}
                onChange={(e) => setEditedComment(e.target.value)}
                autoSize={{ minRows: 1, maxRows: 5 }}
                maxLength={3000}
                style={{
                  border: '1px solid #eee',
                  borderRadius: '10px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: 2,
                  width: '100%',
                  padding: '10px',
                  boxShadow: 'none',
                  marginTop: -40,
                }}
              />
            </div>
          </>
        )) || (
          <div style={{ flexGrow: 1 }}>
            <Input.TextArea
              ref={textareaRef}
              value={editedComment}
              onChange={(e) => setEditedComment(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 5 }}
              maxLength={3000}
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: '10px',
                outline: 'none',
                resize: 'vertical',
                minHeight: 2,
                width: '100%',
                padding: '10px',
                boxShadow: 'none',
              }}
            />
          </div>
        )}
      </FileDropPasteArea>

      <div
        style={{
          marginTop: 8,
          display: 'flex',
          gap: 4,
          marginBottom: 8,
        }}
      >
        <PaperClipOutlined
          style={{
            paddingInline: '5px',
            color: 'gray',
            fontSize: '20px',
          }}
          onClick={handleUploadFileClick}
        />
        <PictureOutlined
          style={{
            paddingInline: '5px',
            color: 'gray',
            fontSize: '20px',
          }}
          onClick={handleUploadImageClick}
        />
        <CodeOutlined
          style={{
            paddingInline: '5px',
            color: 'gray',
            fontSize: '20px',
          }}
          onClick={handleInsertCode}
        />
      </div>

      {/* Save/Cancel buttons */}
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          type="primary"
          onClick={handleSave}
          disabled={buttonDisabled}
          style={{
            marginLeft: '5px',
            fontSize: '13px',
            height: '30px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            color: 'white',
          }}
        >
          Save
        </Button>
        <Button
          onClick={handleCancel}
          disabled={buttonDisabled}
          style={{
            marginLeft: '5px',
            fontSize: '13px',
            height: '30px',
            borderRadius: '5px',
            color: 'black',
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default TaskEditableCommentDetails;
